import Component from "@base/Component.js";
import { loadGoogleMaps, hasMaps } from "@/services/googleMaps.js";

// Minimal warm-grey / off-white style so the map matches the site theme
// (charcoal + gold + cream). POI/transit/admin clutter is hidden.
const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f4f3f0" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a877f" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f4f3f0" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9b9890" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e7e4dc" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dcd9d2" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#b4b0a7" }] },
];

// Teardrop map marker in the theme colours (charcoal pin, gold centre).
// `number` (1-based), when given, is drawn inside the gold centre - used by
// the gallery editor's preview so each pin's number matches its position in
// the place list; the public gallery map never passes it, so its pins are
// unchanged.
function pinSvg(number) {
  const hasNumber = number != null;
  const r = hasNumber ? 7 : 5;
  const label = hasNumber
    ? `<text x="14" y="14.5" text-anchor="middle" dominant-baseline="middle" font-family="sans-serif" font-size="8" font-weight="700" fill="#2b2d31">${number}</text>`
    : "";
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">' +
    '<path d="M14 0C6.27 0 0 6.27 0 14c0 9.5 12.4 24.5 13 25.2a1.3 1.3 0 0 0 2 0C15.6 38.5 28 23.5 28 14 28 6.27 21.73 0 14 0z" fill="#2b2d31"/>' +
    `<circle cx="14" cy="14" r="${r}" fill="#d4b572"/>` +
    label +
    "</svg>"
  );
}

/**
 * Google Map with a marker per place id. Renders nothing if maps are not
 * configured or the gallery has no places. Ports the legacy maps.js behaviour
 * (PlacesService → geometry → markers, fit bounds).
 * props: { placeIds: string[], zoomLevel: number, numbered?: boolean }
 */
export default class MapView extends Component {
  render() {
    return `<div class="map-view"></div>`;
  }

  async onMounted() {
    const placeIds = this.props.placeIds || [];
    if (!hasMaps() || !placeIds.length) {
      this.el.style.display = "none";
      return;
    }

    try {
      const maps = await loadGoogleMaps();

      const map = new maps.Map(this.el, {
        zoom: this.props.zoomLevel || 8,
        center: { lat: 46.8, lng: 8.2 }, // Switzerland default
        styles: MAP_STYLE,
        disableDefaultUI: true, // no zoom / street view / fullscreen / map-type controls
        clickableIcons: false,
        keyboardShortcuts: false,
      });

      const service = new maps.places.PlacesService(map);
      const markerIcon = (number) => ({
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(pinSvg(number)),
        scaledSize: new maps.Size(28, 40),
        anchor: new maps.Point(14, 40), // tip points at the location
      });
      const bounds = new maps.LatLngBounds();
      const positions = [];
      let pending = placeIds.length;

      const finalize = () => {
        if (positions.length === 1) {
          // A single place: center it and keep the gallery's zoom level
          // (fitBounds on one point would zoom all the way in).
          map.setCenter(positions[0]);
          map.setZoom(this.props.zoomLevel || 10);
        } else if (positions.length > 1) {
          map.fitBounds(bounds, 56); // pad so markers aren't on the edge
          // fitBounds picks whatever zoom fits every marker, which can be
          // tighter than the gallery's configured zoom for closely-clustered
          // locations. Cap it post-fit (never zoom in closer than configured)
          // without ever zooming further OUT than fitBounds already computed,
          // so a marker never ends up cut off.
          const maxZoom = this.props.zoomLevel;
          if (maxZoom) {
            maps.event.addListenerOnce(map, "idle", () => {
              if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
            });
          }
        }
      };

      placeIds.forEach((placeId, index) => {
        service.getDetails({ placeId, fields: ["geometry"] }, (place, status) => {
          if (status === "OK" && place?.geometry?.location) {
            new maps.Marker({ map, position: place.geometry.location, icon: markerIcon(this.props.numbered ? index + 1 : null) });
            positions.push(place.geometry.location);
            bounds.extend(place.geometry.location);
          }
          if (--pending === 0) finalize();
        });
      });
    } catch (err) {
      console.warn("Map unavailable:", err.message);
      this.el.style.display = "none";
    }
  }
}
